import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { handleErrors } from '../../../../utils/errors';
import SharedDialog from '../../../../shared/shared-dialog';
import {
  UserKeys,
  bulkChangeGpanelRole,
  getAllGPanelRoles,
} from '../../../../api/users-api';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
interface ChangeRoleProps {
  open: boolean;
  handleClose: () => void;
  selectedUsers: string[];
  setSelectedUsers: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function ChangeRoleDialog({
  open,
  handleClose,
  selectedUsers,
  setSelectedUsers,
  changeLogRoles,
}: ChangeRoleProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [currentRole, setCurrentRole] = useState('User');
  const { data: gPanelRoles } = useQuery({
    queryKey: [UserKeys.gPanelRoles],
    queryFn: () => getAllGPanelRoles(),
    meta: {
      errorMessage:
        'There was an issue loading roles. Please refresh and try again.',
    },
  });

  const { mutate, isPending: isSaving } = useMutation({
    mutationFn: () =>
      bulkChangeGpanelRole(
        'User',
        selectedUsers,
        currentRole,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to change the user(s) gPanel role has started.',
      });
      setChangeLogMessage('');
      setCurrentRole('User');
      setSelectedUsers([]);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue changing the user(s) gPanel role. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.user_management.user_grid.bulk_change_role, {
        module: ModuleList.modules.userManagement.name,
        section: ModuleList.modules.userManagement.name,
      });
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.BulkChangeGpanelRole.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  if (!gPanelRoles) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        sx={{ width: '100%', minHeight: 300 }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        handleClose();
        setCurrentRole('User');
      }}
      title="Change gPanel Role"
      dialogContent={
        <Stack>
          {selectedUsers.length > 1 ? (
            <Typography>
              Change role for <strong>{selectedUsers.length} users</strong>.
            </Typography>
          ) : (
            <Typography>
              Change role for <strong>{selectedUsers[0]}</strong>.
            </Typography>
          )}
          <TextField
            autoFocus
            inputProps={{
              id: 'gpanel-role',
            }}
            InputLabelProps={{
              htmlFor: 'gpanel-role',
            }}
            select
            label="gPanel Role"
            value={currentRole}
            onChange={(event) => setCurrentRole(event.target.value)}
            fullWidth
            sx={{ mt: 2 }}
          >
            {gPanelRoles?.length ? (
              gPanelRoles?.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))
            ) : (
              <MenuItem value={''}>No Roles Found</MenuItem>
            )}
          </TextField>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isSaving}
          />
        </Stack>
      }
      primaryButton={{
        loading: isSaving,
        action: handleSubmit,
        disabled: isSaving,
        title: 'Save Changes',
      }}
    />
  );
}
